import { createContext, use, useCallback, useEffect, useMemo, useState } from 'react';

import type { ReactNode } from 'react';

export type ZIndexLayer = 'context-menu' | 'popover' | 'modal' | 'toast' | 'critical';

interface ZIndexContextValue {
	contextDepth: number;

	/**
	 * Get the current z-index for a specific layer type
	 */
	getZIndex: (layer: ZIndexLayer) => number;

	/**
	 * Push a new layer onto the stack and get its z-index
	 */
	pushLayer: (layer: ZIndexLayer) => number;

	/**
	 * Pop a layer from the stack
	 */
	popLayer: (layer: ZIndexLayer) => void;

	/**
	 * Get the current stack depth for debugging
	 */
	getStackDepth: () => number;
}

const ZIndexContext = createContext<ZIndexContextValue | null>(null);

/**
 * ⚠️ You probably don't need to use this directly. Use `useZIndex` instead.
 *
 * Z-Index Context for managing layered components like popovers, context menus, and modals.
 */
export function useZIndexContext(required?: true): ZIndexContextValue;
export function useZIndexContext(required: false): ZIndexContextValue | undefined;
export function useZIndexContext(required = true): ZIndexContextValue | undefined {
	const context = use(ZIndexContext);
	if (!context && required) {
		throw new Error('useZIndexContext must be used within a ZIndexProvider');
	}
	return context ?? undefined;
}

/**
 * Z-Index Context for managing layered components like popovers, context menus, and modals.
 *
 * This context provides automatic z-index management to ensure proper stacking order
 * when components are nested or opened in sequence.
 *
 * Base z-index values:
 * - 100: Context menus, dropdowns
 * - 200: Popovers, tooltips
 * - 300: Modals, dialogs
 * - 400: Toast notifications
 * - 500: Critical overlays
 */
export function useZIndex(layer: ZIndexLayer): number {
	const { pushLayer, popLayer } = useZIndexContext(false) ?? {
		// If there isn't a ZIndexProvider, just use the base z-index
		pushLayer: () => BASE_Z_INDEX[layer],
		popLayer: () => {},
	};
	const [zIndex] = useState(() => pushLayer(layer));

	// Clean up when component unmounts
	useEffect(() => {
		return () => popLayer(layer);
	}, [layer, popLayer]);

	return zIndex;
}

const BASE_Z_INDEX: Record<ZIndexLayer, number> = {
	'context-menu': 100,
	popover: 200,
	modal: 300,
	toast: 400,
	critical: 500,
};

const LAYER_INCREMENT = 10;
const CONTEXT_DEPTH_INCREMENT = 1000;

export function ZIndexProvider({ children }: { children: ReactNode }) {
	const parentContext = useZIndexContext(false);
	const [layerStack, setLayerStack] = useState<ZIndexLayer[]>([]);
	const contextDepth = (parentContext?.contextDepth ?? 0) + 1;

	const getZIndex = useCallback(
		(layer: ZIndexLayer): number => {
			const baseIndex = BASE_Z_INDEX[layer] + contextDepth * CONTEXT_DEPTH_INCREMENT;
			const stackDepth = layerStack.filter((l) => l === layer).length;
			return baseIndex + stackDepth * LAYER_INCREMENT;
		},
		[layerStack, contextDepth],
	);

	const pushLayer = useCallback(
		(layer: ZIndexLayer): number => {
			const zIndex = getZIndex(layer);
			setLayerStack((prev) => [...prev, layer]);
			return zIndex + LAYER_INCREMENT; // Return the next available z-index
		},
		[getZIndex],
	);

	const popLayer = useCallback((layer: ZIndexLayer): void => {
		setLayerStack((prev) => {
			const lastIndex = prev.lastIndexOf(layer);
			if (lastIndex === -1) {
				return prev;
			}
			return [...prev.slice(0, lastIndex), ...prev.slice(lastIndex + 1)];
		});
	}, []);

	const getStackDepth = useCallback((): number => {
		return layerStack.length;
	}, [layerStack]);

	const contextValue = useMemo(
		(): ZIndexContextValue => ({
			contextDepth,
			getZIndex,
			pushLayer,
			popLayer,
			getStackDepth,
		}),
		[contextDepth, getZIndex, pushLayer, popLayer, getStackDepth],
	);

	return <ZIndexContext.Provider value={contextValue}>{children}</ZIndexContext.Provider>;
}
